// src/pages/DonatePage.tsx
// Public donation page — Stripe slot (Zeffy after 501(c)(3) recognition).
// Tiers priced to REAL costs so every suggested amount is auditable.
// See CONTENT_FACTS.md for approved public-facing status language.

import { Link } from 'react-router-dom';
import { Heart, Laptop, Cloud, GraduationCap, ShieldCheck } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import BackToTop from '../components/ui/BackToTop';

interface DonationTier {
  id: string;
  amount: string;
  /** What this dollar amount actually buys — must map to real, current costs */
  funds: string;
  icon: typeof Heart;
}

// TODO(hunter): verify each figure against actual invoices/quotes before launch.
// If a number can't be backed up, change the number — not the honesty.
const tiers: DonationTier[] = [
  {
    id: 'community',
    amount: '$50',
    funds: 'One month of community platform tooling for the learner Slack ecosystem',
    icon: Heart,
  },
  {
    id: 'infrastructure',
    amount: '$150',
    funds: 'One month of cloud infrastructure powering the AI mentor and learning platform',
    icon: Cloud,
  },
  {
    id: 'curriculum',
    amount: '$500',
    funds: 'Development of one complete curriculum module, from outline to assessments',
    icon: GraduationCap,
  },
  {
    id: 'laptop',
    amount: '$1,200',
    funds: 'A refurbished development laptop and peripherals for one incoming learner',
    icon: Laptop,
  },
];

const DonatePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      <main id="main-content">
        {/* Hero — one job: state what a gift does, then get out of the way */}
        <section
          aria-labelledby="donate-heading"
          className="bg-secondary-950 py-20 sm:py-28"
        >
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1
              id="donate-heading"
              className="font-display text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight tracking-tight"
            >
              Fund a Second Story
            </h1>
            <p className="text-lg sm:text-xl text-secondary-300 leading-relaxed max-w-2xl mx-auto">
              Every dollar builds the tools, curriculum, and community that help
              justice-impacted individuals launch careers in technology. Below,
              we show you exactly what your gift pays for.
            </p>
          </div>
        </section>

        {/* The honest menu — tiers mapped to real costs */}
        <section aria-labelledby="tiers-heading" className="py-16 sm:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2
              id="tiers-heading"
              className="text-2xl font-semibold text-secondary-900 text-center mb-3"
            >
              What your gift builds
            </h2>
            <p className="text-secondary-600 text-center mb-12 max-w-2xl mx-auto">
              These amounts map to our actual operating costs — see the full
              breakdown on our{' '}
              <Link to="/about" className="text-primary-700 underline">
                budget and transparency page
              </Link>
              .
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {tiers.map((tier) => (
                <div
                  key={tier.id}
                  className="rounded-xl border border-secondary-200 p-6 text-center shadow-sm"
                >
                  <tier.icon
                    className="h-8 w-8 text-primary-700 mx-auto mb-4"
                    aria-hidden="true"
                  />
                  <p className="text-3xl font-bold text-secondary-900 mb-2">
                    {tier.amount}
                  </p>
                  <p className="text-sm text-secondary-600 leading-relaxed">
                    {tier.funds}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Donation form — Stripe embed slot */}
        <section
          aria-labelledby="give-heading"
          className="py-16 bg-secondary-50"
        >
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2
              id="give-heading"
              className="text-2xl font-semibold text-secondary-900 text-center mb-8"
            >
              Make your gift
            </h2>

            {/*
              DONATION FORM SLOT — Stripe + PayPal
              DO NOT GO LIVE until:
              1. Form 1023-EZ filed
              2. RI charitable solicitation registration complete
              Replace STRIPE_PAYMENT_LINK_URL and PAYPAL_DONATE_URL
              with actual URLs once accounts are provisioned.
              SWAP LATER: once the IRS determination letter arrives, replace
              with the Zeffy embed and update the trust block to tax-deductible.
            */}
            <div className="rounded-xl border border-secondary-200 bg-white p-8 sm:p-12">
              {/* Status banner — remove once payment links are live */}
              <div className="rounded-lg bg-accent-50 border border-accent-200 p-4 mb-8 text-center">
                <p className="text-sm font-medium text-accent-800">
                  Online donation processing is being finalized. In the
                  meantime, please{' '}
                  <a
                    href="mailto:eHunter@straydog-secondstory.org?subject=Donation%20Inquiry"
                    className="text-accent-900 underline font-semibold"
                  >
                    contact us directly
                  </a>{' '}
                  to make a contribution.
                </p>
              </div>

              <div className="text-center mb-8">
                <p className="text-secondary-600 max-w-md mx-auto">
                  We will accept donations via Stripe and PayPal once our
                  regulatory filings are complete.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 max-w-lg mx-auto opacity-50 pointer-events-none" aria-disabled="true">
                {/* Stripe Payment Link — DISABLED until live */}
                <div
                  className="flex flex-col items-center gap-3 rounded-xl border-2 border-secondary-200 p-6"
                >
                  <div className="h-12 w-12 rounded-full bg-[#635BFF]/10 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="h-6 w-6 text-[#635BFF]" fill="currentColor" aria-hidden="true">
                      <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-7.076-2.304l-.89 5.584c1.953 1.089 5.356 1.9 8.037 1.9 2.59 0 4.749-.631 6.298-1.932 1.72-1.426 2.564-3.493 2.564-5.98.014-4.061-2.543-5.72-6.281-6.683z" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold text-secondary-900">
                    Pay with Card
                  </span>
                  <span className="text-xs text-secondary-500">
                    Via Stripe — Coming Soon
                  </span>
                </div>

                {/* PayPal Donate — DISABLED until live */}
                <div
                  className="flex flex-col items-center gap-3 rounded-xl border-2 border-secondary-200 p-6"
                >
                  <div className="h-12 w-12 rounded-full bg-[#0070BA]/10 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="h-6 w-6 text-[#0070BA]" fill="currentColor" aria-hidden="true">
                      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 2.73A.77.77 0 0 1 5.704 2h6.635c3.229 0 5.503 2.106 5.092 5.37-.533 4.233-3.602 5.63-6.727 5.63H8.467l-1.39 8.337zm12.048-14.2c-.27 2.164-1.61 4.283-5.092 4.283H12.37l1.069-6.4h1.663c2.252 0 4.139.54 4.022 2.117zM20.07 8.5c-.533 4.233-3.602 5.996-6.727 5.996h-1.734l-1.39 8.337h-2.59L8.7 16.496h2.462c3.229 0 6.16-1.763 6.694-5.996.27-2.164-.497-3.413-1.82-4.075A6.322 6.322 0 0 1 20.07 8.5z" />
                    </svg>
                  </div>
                  <span className="text-sm font-semibold text-secondary-900">
                    Pay with PayPal
                  </span>
                  <span className="text-xs text-secondary-500">
                    Or Venmo — Coming Soon
                  </span>
                </div>
              </div>
            </div>

            {/* Trust block — HONEST status. Do not claim recognized 501(c)(3)
                or tax-deductibility until the IRS determination letter arrives.
                See CONTENT_FACTS.md. Swap this copy on the day it does. */}
            <div className="mt-8 flex items-start gap-3 text-sm text-secondary-600">
              <ShieldCheck
                className="h-5 w-5 text-primary-700 flex-shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <p>
                The Second Story Initiative Inc. is a Rhode Island nonprofit
                corporation (EIN 42-2812566). Our application for federal
                501(c)(3) tax-exempt status is in preparation. Gifts made now
                support our mission directly; they are{' '}
                <strong>not yet tax-deductible</strong> and will become
                deductible retroactively if and when the IRS grants
                recognition. We will email you a confirmation of your gift.
              </p>
            </div>
          </div>
        </section>

        {/* Bridge to the work — donors who hesitate want proof, not pressure */}
        <section className="py-16 text-center">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-semibold text-secondary-900 mb-4">
              See what we're building
            </h2>
            <p className="text-secondary-600 mb-8">
              Before you give, look at the work. Our projects — from the AI
              Orchestrator to the learning platform — are documented in the
              open, progress and all.
            </p>
            <Link
              to="/projects"
              className="btn btn-primary btn-lg inline-flex items-center no-underline hover:no-underline"
            >
              Explore our projects
            </Link>
          </div>
        </section>
      </main>

      <PublicFooter />
      <BackToTop />
    </div>
  );
};

export default DonatePage;
